import { useCallback, useRef } from 'react';
import { useLocalState } from '../pages/Agent/ChatPage/hooks/useLocalState';
import { getCOSPresigned } from '../pages/Agent/service/service';
import { isDataBrainUrl } from '../pages/Agent/ChatDSTemplate/utils';

/**
 * 图片签名缓存项接口
 */
interface ICacheItem {
  url: string;
  timestamp: number;
}

/**
 * 图片缓存数据类型
 */
interface ICacheData {
  [originalUrl: string]: ICacheItem;
}

/**
 * 基于 useLocalState 的图片缓存 Hook
 * 使用内存存储进行全局图片签名缓存
 */
export const useImageCacheLocal = () => {
  // const defaultExpiryTime = 2 * 60 * 60 * 1000 - 10 * 60 * 1000; // 默认 2 小时 - 10分钟
  const HOURS_TO_MS = 60 * 60 * 1000;
  const MINUTES_TO_MS = 60 * 1000;
  const defaultExpiryTime = 2 * HOURS_TO_MS - 10 * MINUTES_TO_MS;
  const cacheKey = 'aigc_image_cache';
  
  // 使用 useLocalState 管理缓存数据
  const [cacheData, setCacheData] = useLocalState<ICacheData>(cacheKey, {});
  
  // 清理定时器引用
  const cleanupTimerRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * 检查缓存项是否有效
   */
  const isCacheValid = useCallback((cachedItem: ICacheItem, expiryTime?: number): boolean => {
    const now = Date.now();
    const actualExpiryTime = expiryTime || defaultExpiryTime;
    return (now - cachedItem.timestamp) < actualExpiryTime;
  }, [defaultExpiryTime]);

  /**
   * 获取缓存的签名URL
   */
  const getCachedUrl = useCallback((originalUrl: string, expiryTime?: number): string | null => {
    const cachedItem = cacheData[originalUrl];
    if (!cachedItem) {
      return null;
    }

    if (isCacheValid(cachedItem, expiryTime)) {
      return cachedItem.url;
    }

    // 缓存过期，移除该缓存项
    setCacheData(prev => {
      const newCache = { ...prev };
      delete newCache[originalUrl];
      return newCache;
    });
    
    return null;
  }, [cacheData, isCacheValid, setCacheData]);

  /**
   * 设置缓存的签名URL
   */
  const setCachedUrl = useCallback((originalUrl: string, signedUrl: string): void => {
    setCacheData(prev => ({
      ...prev,
      [originalUrl]: {
        url: signedUrl,
        timestamp: Date.now(),
      },
    }));
  }, [setCacheData]);

  /**
   * 获取签名URL（带缓存）
   * @param originalUrl 原始URL
   * @param expiryTime 缓存过期时间（可选）
   * @param forceSign 是否强制签名（可选），为 true 时跳过 isDataBrainUrl 判断直接签名
   */
  const getSignedUrl = useCallback(async (originalUrl: string, expiryTime?: number, forceSign?: boolean): Promise<string> => {
    // 先检查缓存
    const cachedUrl = getCachedUrl(originalUrl, expiryTime);
    if (cachedUrl) {
      return cachedUrl;
    }

    // 如果是 databrain URL 或强制签名，进行签名
    if (forceSign || isDataBrainUrl(originalUrl)) {
      try {
        const res = await getCOSPresigned({
          "cos_type": "agentCos",
          "url": [originalUrl]
        }) as { url?: string[]; };
        
        const signedUrl = res?.url?.[0];
        if (signedUrl) {
          // 缓存签名URL
          setCachedUrl(originalUrl, signedUrl);
          return signedUrl;
        }
        throw new Error('签名失败：未获取到有效的签名URL');
      } catch (error) {
        console.warn('Failed to sign URL:', originalUrl, error);
        throw error;
      }
    }
    
    // 非 databrain URL 直接返回
    return originalUrl;
  }, [getCachedUrl, setCachedUrl]);

  /**
   * 清理过期缓存
   */
  const cleanupExpiredCache = useCallback((expiryTime?: number) => {
    const actualExpiryTime = expiryTime || defaultExpiryTime;
    const now = Date.now();
    
    setCacheData(prev => {
      const newCache = { ...prev };
      let hasChanges = false;
      
      for (const [url, item] of Object.entries(newCache)) {
        const itemAge = now - item.timestamp;
        if (itemAge > actualExpiryTime) {
          delete newCache[url];
          hasChanges = true;
        }
      }
      
      return hasChanges ? newCache : prev;
    });
  }, [defaultExpiryTime, setCacheData]);

  /**
   * 清除所有缓存
   */
  const clearAllCache = useCallback(() => {
    setCacheData({});
  }, [setCacheData]);

  /**
   * 获取缓存统计信息
   */
  const getCacheStats = useCallback(() => {
    const now = Date.now();
    let valid = 0;
    let expired = 0;

    for (const item of Object.values(cacheData)) {
      const itemAge = now - item.timestamp;
      if (itemAge > defaultExpiryTime) {
        expired += 1;
      } else {
        valid += 1;
      }
    }

    return {
      total: Object.keys(cacheData).length,
      valid,
      expired,
    };
  }, [cacheData, defaultExpiryTime]);

  /**
   * 启动定期清理过期缓存
   */
  const startPeriodicCleanup = useCallback((interval: number = 30 * 60 * 1000) => {
    if (cleanupTimerRef.current) {
      clearInterval(cleanupTimerRef.current);
    }
    
    cleanupTimerRef.current = setInterval(() => {
      cleanupExpiredCache();
    }, interval);
  }, [cleanupExpiredCache]);

  /**
   * 停止定期清理
   */
  const stopPeriodicCleanup = useCallback(() => {
    if (cleanupTimerRef.current) {
      clearInterval(cleanupTimerRef.current);
      cleanupTimerRef.current = null;
    }
  }, []);

  return {
    getSignedUrl,
    getCachedUrl,
    setCachedUrl,
    cleanupExpiredCache,
    clearAllCache,
    getCacheStats,
    startPeriodicCleanup,
    stopPeriodicCleanup,
    cacheData,
  };
};

export default useImageCacheLocal;
